import { io, type Socket } from 'socket.io-client';

type RealtimeHandler = (...args: unknown[]) => void;

let socket: Socket | null = null;

// Handlers live here, not on the socket: a branch switch or a refused session tears the socket
// down and builds a new one, and every subscriber must follow it without subscribing again.
const handlers = new Map<string, Set<RealtimeHandler>>();

function resolveSocketConfig(): { url: string; path: string } {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api';

  let origin: string;
  let apiPath: string;

  try {
    const parsed = new URL(apiBaseUrl, window.location.origin);
    origin = parsed.origin;
    apiPath = parsed.pathname;
  } catch {
    origin = window.location.origin;
    apiPath = apiBaseUrl;
  }

  const normalizedApiPath = apiPath.replace(/\/$/, '');

  return { url: origin, path: `${normalizedApiPath}/socket.io` };
}

export function connectRealtime(): void {
  if (socket) return;

  const { url, path } = resolveSocketConfig();
  const created = io(url, {
    path,
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });
  socket = created;

  for (const [event, eventHandlers] of handlers) {
    for (const handler of eventHandlers) created.on(event, handler);
  }
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
}

export function isRealtimeActive(): boolean {
  return socket?.active ?? false;
}

export function onRealtimeEvent<Args extends unknown[]>(
  event: string,
  handler: (...args: Args) => void
): () => void {
  const stored = handler as unknown as RealtimeHandler;
  const eventHandlers = handlers.get(event) ?? new Set<RealtimeHandler>();
  eventHandlers.add(stored);
  handlers.set(event, eventHandlers);
  socket?.on(event, stored);

  return () => {
    handlers.get(event)?.delete(stored);
    socket?.off(event, stored);
  };
}

export function emitRealtimeEvent(
  event: string,
  payload: unknown,
  ack?: (reply: unknown) => void
): boolean {
  if (!socket?.connected) return false;

  if (ack) socket.emit(event, payload, ack);
  else socket.emit(event, payload);
  return true;
}
